import { useMemo, useCallback } from "react";
import { Plus, Trash2 } from "lucide-react";

/**
 * Percentage Rent configuration tab for the tenant detail panel.
 *
 * Supports three modes:
 *  - None: no percentage rent
 *  - Simple: single breakpoint + rate
 *  - Detailed: time-series table of breakpoint/rate pairs
 */

export interface PercentageRentEntry {
  id: string;
  start_date: string;
  end_date: string;
  breakpoint: number | null;
  rate_pct: number | null;
}

export type PercentageRentType = "none" | "simple" | "detailed";

interface PercentageRentConfig {
  type: PercentageRentType;
  breakpoint: number | null;
  rate_pct: number | null;
  entries: PercentageRentEntry[];
}

interface PercentageRentTabProps {
  config: PercentageRentConfig;
  onTypeChange: (type: PercentageRentType) => void;
  onSimpleChange: (key: "breakpoint" | "rate_pct", value: number | null) => void;
  onEntryChange: (id: string, key: keyof PercentageRentEntry, value: unknown) => void;
  onEntryAdd: () => void;
  onEntryDelete: (id: string) => void;
}

export default function PercentageRentTab({
  config,
  onTypeChange,
  onSimpleChange,
  onEntryChange,
  onEntryAdd,
  onEntryDelete,
}: PercentageRentTabProps) {
  const handleTypeChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      onTypeChange(e.target.value as PercentageRentType);
    },
    [onTypeChange],
  );

  const entries = useMemo(() => config.entries, [config.entries]);

  return (
    <div className="space-y-5">
      <div className="bg-white rounded-xl border border-grove-300 p-5 shadow-sm">
        <h3 className="text-xs font-semibold uppercase tracking-wider text-bark-400 mb-4">
          Percentage Rent Configuration
        </h3>

        {/* Type selector */}
        <div className="flex items-center gap-4 mb-5">
          <label className="text-sm text-bark-600 whitespace-nowrap">Type</label>
          <select
            value={config.type}
            onChange={handleTypeChange}
            className="w-48 px-2.5 py-1.5 text-sm bg-white border border-grove-300 rounded-lg text-bark-700 focus:ring-1 focus:ring-forest-500 focus:border-forest-500"
          >
            <option value="none">None</option>
            <option value="simple">Simple</option>
            <option value="detailed">Detailed</option>
          </select>
        </div>

        {/* Simple mode: single breakpoint + rate */}
        {config.type === "simple" && (
          <div className="grid grid-cols-2 gap-4 max-w-md">
            <div>
              <label className="block text-xs font-medium text-bark-500 mb-1">
                Breakpoint ($)
              </label>
              <div className="relative">
                <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-sm text-bark-400">
                  $
                </span>
                <input
                  type="number"
                  step="any"
                  value={config.breakpoint ?? ""}
                  onChange={(e) =>
                    onSimpleChange(
                      "breakpoint",
                      e.target.value ? Number(e.target.value) : null,
                    )
                  }
                  placeholder="0"
                  className="w-full pl-6 pr-2.5 py-1.5 text-sm text-right border border-grove-300 rounded-lg text-bark-700 tabular-nums focus:ring-1 focus:ring-forest-500 focus:border-forest-500"
                />
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-bark-500 mb-1">
                Rate (%)
              </label>
              <div className="relative">
                <input
                  type="number"
                  step="any"
                  value={config.rate_pct ?? ""}
                  onChange={(e) =>
                    onSimpleChange(
                      "rate_pct",
                      e.target.value ? Number(e.target.value) : null,
                    )
                  }
                  placeholder="0.0"
                  className="w-full pl-2.5 pr-6 py-1.5 text-sm text-right border border-grove-300 rounded-lg text-bark-700 tabular-nums focus:ring-1 focus:ring-forest-500 focus:border-forest-500"
                />
                <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-sm text-bark-400">
                  %
                </span>
              </div>
            </div>
          </div>
        )}

        {/* None mode */}
        {config.type === "none" && (
          <p className="text-sm text-bark-400">
            No percentage rent configured for this tenant.
          </p>
        )}
      </div>

      {/* Detailed mode: time-series table */}
      {config.type === "detailed" && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="text-xs font-semibold uppercase tracking-wider text-bark-400">
              Percentage Rent Schedule
            </h3>
            <button
              type="button"
              onClick={onEntryAdd}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-forest-700 text-white rounded-lg hover:bg-forest-800 transition-colors"
            >
              <Plus className="w-3.5 h-3.5" />
              Add Period
            </button>
          </div>

          <div className="bg-white border border-grove-300 rounded-xl overflow-hidden shadow-sm">
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="bg-grove-100 border-b border-grove-300">
                    <th className="px-3 py-2 text-xs font-semibold text-bark-500 uppercase tracking-wider text-left w-[130px]">
                      Start Date
                    </th>
                    <th className="px-3 py-2 text-xs font-semibold text-bark-500 uppercase tracking-wider text-left w-[130px]">
                      End Date
                    </th>
                    <th className="px-3 py-2 text-xs font-semibold text-bark-500 uppercase tracking-wider text-right w-[140px]">
                      Breakpoint ($)
                    </th>
                    <th className="px-3 py-2 text-xs font-semibold text-bark-500 uppercase tracking-wider text-right w-[100px]">
                      Rate (%)
                    </th>
                    <th className="w-10 px-2 py-2" />
                  </tr>
                </thead>
                <tbody>
                  {entries.length === 0 ? (
                    <tr>
                      <td
                        colSpan={5}
                        className="px-4 py-8 text-center text-sm text-bark-400"
                      >
                        No periods yet. Click "+ Add Period" to add one.
                      </td>
                    </tr>
                  ) : (
                    entries.map((entry) => (
                      <tr
                        key={entry.id}
                        className="border-b border-grove-200 last:border-0"
                      >
                        <td className="px-3 py-1.5">
                          <input
                            type="date"
                            value={entry.start_date}
                            onChange={(e) =>
                              onEntryChange(entry.id, "start_date", e.target.value)
                            }
                            className="w-full px-2 py-1 text-sm border border-grove-300 rounded-lg text-bark-700 focus:ring-1 focus:ring-forest-500 focus:border-forest-500"
                          />
                        </td>
                        <td className="px-3 py-1.5">
                          <input
                            type="date"
                            value={entry.end_date}
                            onChange={(e) =>
                              onEntryChange(entry.id, "end_date", e.target.value)
                            }
                            className="w-full px-2 py-1 text-sm border border-grove-300 rounded-lg text-bark-700 focus:ring-1 focus:ring-forest-500 focus:border-forest-500"
                          />
                        </td>
                        <td className="px-3 py-1.5">
                          <div className="relative">
                            <span className="absolute left-2 top-1/2 -translate-y-1/2 text-sm text-bark-400">
                              $
                            </span>
                            <input
                              type="number"
                              step="any"
                              value={entry.breakpoint ?? ""}
                              onChange={(e) =>
                                onEntryChange(
                                  entry.id,
                                  "breakpoint",
                                  e.target.value ? Number(e.target.value) : null,
                                )
                              }
                              className="w-full pl-5 pr-2 py-1 text-sm text-right border border-grove-300 rounded-lg text-bark-700 tabular-nums focus:ring-1 focus:ring-forest-500 focus:border-forest-500"
                            />
                          </div>
                        </td>
                        <td className="px-3 py-1.5">
                          <div className="relative">
                            <input
                              type="number"
                              step="any"
                              value={entry.rate_pct ?? ""}
                              onChange={(e) =>
                                onEntryChange(
                                  entry.id,
                                  "rate_pct",
                                  e.target.value ? Number(e.target.value) : null,
                                )
                              }
                              className="w-full pl-2 pr-5 py-1 text-sm text-right border border-grove-300 rounded-lg text-bark-700 tabular-nums focus:ring-1 focus:ring-forest-500 focus:border-forest-500"
                            />
                            <span className="absolute right-2 top-1/2 -translate-y-1/2 text-sm text-bark-400">
                              %
                            </span>
                          </div>
                        </td>
                        <td className="px-2 py-1.5 text-center">
                          <button
                            type="button"
                            onClick={() => onEntryDelete(entry.id)}
                            className="p-1 rounded text-bark-300 hover:text-red-500 hover:bg-red-50 transition-colors"
                            title="Delete period"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
